
import useShippingZonesController from "@/hooks/useShippingZonesController";
import { Dialog, DialogContent } from "@/shared";
import Loader from "@/shared/components/ui/Loader";
import { motion } from 'framer-motion';
import { Pencil, Plus, Trash } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function ShippingZonesManagement() {
    const { t } = useTranslation();
    const {
        places,
        isLoading,
        placesQuery,
        managementDialog,
        isUpdating,
        isDeleting,
        isInserting,
        handleSubmitInsertPlace,
        handleSubmitUpdatePlace,
        handleChange,
        setManagementDialog,
        handleSubmitDeletePlace,
        handleExport
    } = useShippingZonesController();
    return (
        <>
            {/* <Header title={t("shipping_zones_management")} info={placesQuery} /> */}
            <div className="mb-4 px-4 pt-8 flex justify-between items-center">
                <button
                    onClick={() => setManagementDialog({ type: 'insert', address_name: '', shipping_fees: 0 })}
                    className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl shadow-sm shadow-emerald-200 transition-all active:scale-95 text-sm font-bold"
                >
                    <Plus className="w-4 h-4" />
                    {t("add_new_zone")}
                </button>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => handleExport('pdf', 'تقرير مناطق التوصيل')}
                        className="bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow-sm transition-colors"
                    >

                        تصدير PDF
                    </button>
                    <button
                        onClick={() => handleExport('excel', 'تقرير مناطق التوصيل')}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow-sm transition-colors"
                    >

                        تصدير Excel
                    </button>
                </div>
            </div>

            {isLoading ? <Loader /> : (
                <>
                    <div className="w-full max-w-7xl">
                        <div className="bg-white rounded-[1rem] border border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-right border-collapse">
                                    <thead>
                                        <tr className="text-slate-900 uppercase font-black tracking-widest border-b border-slate-100 bg-slate-50/30">
                                            <th className="px-6 py-4">{t("addressid")}</th>
                                            <th className="px-6 py-4">{t("addressstring")}</th>
                                            <th className="px-6 py-4">{t("privilege")}</th>
                                            <th className="px-6 py-4">{t("actions")}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {places.length === 0 ? (
                                            <tr>
                                                <td
                                                    colSpan={9}
                                                    className="px-6 py-12 text-center text-slate-900 text-sm italic"
                                                >
                                                    {t("no_available_data")}
                                                </td>
                                            </tr>
                                        ) : (
                                            places?.map((row: any, index: number) => (
                                                <motion.tr
                                                    key={index}
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    className="hover:bg-slate-50/50 transition-colors">
                                                    <td className="px-6 py-4 text-sm font-bold text-slate-900">{row?.addressid || "---"}#</td>
                                                    <td className="px-6 py-4 text-sm text-slate-900">{row?.addressstring || "---"}</td>
                                                    <td className="px-6 py-4 text-sm text-slate-900">{row?.privilege || "---"}</td>
                                                    <td className="px-6 py-4 text-sm text-slate-900">
                                                        <div className="flex gap-3">
                                                            <button
                                                                className="flex items-center"
                                                                onClick={() => {
                                                                    setManagementDialog({
                                                                        type: 'update',
                                                                        id: row?.addressid,
                                                                        address_name: row?.addressstring,
                                                                        shipping_fees: row?.privilege
                                                                    });
                                                                }}>
                                                                <Pencil className="w-4 h-4 text-blue-600" />
                                                            </button>
                                                            <button
                                                                className="flex items-center"
                                                                onClick={() => {
                                                                    setManagementDialog({
                                                                        type: 'delete',
                                                                        id: row?.addressid,
                                                                    });
                                                                }}>
                                                                <Trash className="w-4 h-4 text-red-600" />
                                                            </button>
                                                        </div>

                                                    </td>
                                                </motion.tr>
                                            )))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </>
            )}

            <Dialog open={managementDialog !== null}
                onOpenChange={(open) => { if (!open) setManagementDialog(null); }}
            >
                {managementDialog && <DialogContent className="max-w-md">
                    {managementDialog.type === 'update' ? (
                        <div className="flex flex-col gap-4 p-4">
                            <h3 className="text-lg font-bold">{t("edit_zone")}</h3>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-semibold text-slate-900 mb-1.5">{t("addressstring")}</label>
                                    <input
                                        value={managementDialog.address_name}
                                        onChange={(e) => handleChange(e, 'address_name')}
                                        type="text"
                                        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-emerald-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-slate-900 mb-1.5">{t("shipping_fees")}</label>
                                    <input
                                        value={managementDialog.shipping_fees}
                                        onChange={(e) => handleChange(e, 'shipping_fees')}
                                        type="number"
                                        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-emerald-500"
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end gap-2 mt-4">
                                <button
                                    onClick={() => setManagementDialog(null)}
                                    className="px-4 py-2 text-sm text-slate-900 hover:bg-slate-100 rounded-lg"
                                >
                                    {t("cancel")}
                                </button>
                                <button
                                    className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                                    onClick={handleSubmitUpdatePlace}
                                    disabled={isUpdating}
                                >
                                    {isUpdating ? (
                                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                        </svg>
                                    ) : t("save")}
                                </button>
                            </div>
                        </div>
                    ) : managementDialog.type === 'insert' ?
                        (
                            <div className="flex flex-col gap-4 p-4">
                                <h3 className="text-lg font-bold">{t("add_zone")}</h3>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-900 mb-1.5">{t("addressstring")}</label>
                                        <input
                                            value={managementDialog.address_name}
                                            onChange={(e) => handleChange(e, 'address_name')}
                                            type="text"
                                            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-emerald-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-slate-900 mb-1.5">{t("shipping_fees")}</label>
                                        <input
                                            value={managementDialog.shipping_fees}
                                            onChange={(e) => handleChange(e, 'shipping_fees')}
                                            type="number"
                                            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-emerald-500"
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-end gap-2 mt-4">
                                    <button
                                        onClick={() => setManagementDialog(null)}
                                        className="px-4 py-2 text-sm text-slate-900 hover:bg-slate-100 rounded-lg"
                                    >
                                        {t("cancel")}
                                    </button>
                                    <button
                                        className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                                        onClick={handleSubmitInsertPlace}
                                        disabled={isInserting}
                                    >
                                        {isInserting ? (
                                            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                            </svg>
                                        ) : t("save")}
                                    </button>
                                </div>
                            </div>
                        )

                        : (
                            <div className="p-6">
                                <div className="flex justify-center mb-5">
                                    <div className="bg-red-50 p-3 rounded-full">
                                        <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                    </div>
                                </div>

                                <div className="text-center mb-8">
                                    <h3 className="text-xl font-bold text-slate-900 mb-2">
                                        {t("delete_zone")}
                                    </h3>

                                </div>

                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setManagementDialog(null)}
                                        disabled={isDeleting}
                                        className="flex-1 px-4 py-3 rounded-xl border border-slate-200 text-slate-900 text-sm font-bold hover:bg-slate-50 active:scale-95 transition-all disabled:opacity-50"
                                    >
                                        {t("no")}
                                    </button>

                                    <button
                                        onClick={handleSubmitDeletePlace}
                                        disabled={isDeleting}
                                        className="flex-1 px-4 py-3 rounded-xl bg-red-600 text-white text-sm font-bold hover:bg-red-700 active:scale-95 transition-all shadow-lg shadow-red-100 flex items-center justify-center gap-2 disabled:bg-red-300"
                                    >
                                        {isDeleting ? (
                                            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                            </svg>
                                        ) : (
                                            <>
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                                {t("yes_delete")}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>

                        )}
                </DialogContent>}

            </Dialog>

        </ >
    );
}